import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  EntityType,
  PendingEntry,
  PendingEntryStatus,
  WorkflowQuery,
  WorkflowResponse,
} from '../../types/workflow';
import { workflowService } from '../../services/workflowService';
import { asNumber, asOneOf, useUrlState, useUrlWriter } from '../../hooks/useUrlState';
import { QuickDecision, ReviewQueue } from './ReviewQueue';
import { entryName, getEntityTypeLabel } from './workflowFormat';

const DEFAULT_LIMIT = 10;

/* One codec per parameter, made once: the URL state reads them on every render */
const STATUS = asOneOf<PendingEntryStatus | 'all'>([...Object.values(PendingEntryStatus), 'all']);
const TYPE = asOneOf<EntityType | ''>(Object.values(EntityType));
const PAGE = asNumber(1);
const LIMIT = asNumber(1, 100);

interface PendingEntriesListProps {
  onSelectEntry?: (entry: PendingEntry) => void;
  showFilters?: boolean;
  assignedToMe?: boolean;
  /** Called after an entry is approved or rejected from the list */
  onReviewed?: () => void;
  /** Names this queue's URL parameters; two queues share one address bar */
  scope?: string;
}

export const PendingEntriesList: React.FC<PendingEntriesListProps> = ({
  onSelectEntry,
  showFilters = true,
  assignedToMe = false,
  onReviewed,
  scope = 'entries',
}) => {
  const [entries, setEntries] = useState<PendingEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [acting, setActing] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0,
  });

  // The queue someone is looking at is in the address bar, under names of its
  // own so that two queues on one screen do not overwrite each other
  const [status] = useUrlState<PendingEntryStatus | 'all'>(`${scope}Status`, PendingEntryStatus.PENDING, STATUS);
  const [entityType] = useUrlState<EntityType | ''>(`${scope}Type`, '', TYPE);
  const [page, setPage] = useUrlState(`${scope}Page`, 1, PAGE);
  const [limit] = useUrlState(`${scope}Limit`, DEFAULT_LIMIT, LIMIT);

  const filters = useMemo<WorkflowQuery>(
    () => ({
      status: status === 'all' ? undefined : status,
      entityType: entityType || undefined,
      page,
      limit,
    }),
    [status, entityType, page, limit],
  );

  const loadEntries = useCallback(async (quiet = false) => {
    try {
      if (!quiet) setLoading(true);
      setError(null);

      let response: WorkflowResponse<PendingEntry>;

      if (assignedToMe) {
        const myAssignments = await workflowService.getMyAssignments(filters);
        response = myAssignments.pendingEntries;
      } else {
        response = await workflowService.getPendingEntries(filters);
      }

      setEntries(response.data);
      setPagination({
        page: response.page,
        limit: response.limit,
        total: response.total,
        totalPages: response.totalPages,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load pending entries');
    } finally {
      setLoading(false);
    }
  }, [filters, assignedToMe]);

  useEffect(() => {
    loadEntries();
  }, [loadEntries]);

  /* Changing a filter starts again at page one — one change, not two */
  const write = useUrlWriter();
  const handleFilterChange = (newFilters: Partial<WorkflowQuery>) => {
    const next = { ...filters, ...newFilters };
    write({
      // No status at all means every status, which is not the default
      [`${scope}Status`]: next.status === undefined ? 'all' : next.status === PendingEntryStatus.PENDING ? null : next.status,
      [`${scope}Type`]: next.entityType ?? null,
      [`${scope}Limit`]: next.limit && next.limit !== DEFAULT_LIMIT ? String(next.limit) : null,
      [`${scope}Page`]: null,
    });
  };

  const handleDecide = async (id: string, status: QuickDecision) => {
    setActing(`${id}:${status}`);
    setActionError(null);
    try {
      await workflowService.reviewPendingEntry(id, { status });
      await loadEntries(true);
      onReviewed?.();
    } catch (err: any) {
      setActionError(err?.response?.data?.message || (err instanceof Error ? err.message : 'Failed to submit review'));
    } finally {
      setActing(null);
    }
  };

  return (
    <ReviewQueue
      rows={entries.map(entry => ({
        id: entry.id,
        name: entryName(entry),
        typeLabel: getEntityTypeLabel(entry.entityType),
        entityType: entry.entityType,
        by: entry.submitter?.email,
        at: entry.submittedAt,
        assignee: entry.assignee?.email,
        status: entry.status,
      }))}
      loading={loading}
      error={error}
      onRetry={() => loadEntries()}
      showFilters={showFilters}
      filters={filters}
      onFilterChange={handleFilterChange}
      pagination={pagination}
      onPageChange={setPage}
      onOpen={id => {
        const entry = entries.find(e => e.id === id);
        if (entry) onSelectEntry?.(entry);
      }}
      onDecide={handleDecide}
      acting={acting}
      actionError={actionError}
      emptyLabel={filters.status === PendingEntryStatus.PENDING ? 'Nothing pending' : 'No entries'}
      byLabel="Submitted by"
    />
  );
};
